import React, { useState, useEffect } from 'react';
import { useSearchParams, useNavigate } from 'react-router-dom';
import { Card, Row, Col } from 'react-bootstrap';
import { FiArrowLeft } from 'react-icons/fi';
import axiosInstance from '../../services/AxiosInstance';
import {
  getCheckoutIdFromSearchParams,
  clearPaymenntCheckoutSession,
  PAYMENNT_CHECKOUT_ID_KEY
} from '../../utils/paymentCheckoutParams';
import { clearSubscriptionCache } from '../../utils/subscriptionCache';
import { clearSubscriptionCache as clearFeatureCache } from '../../utils/featureLock';
import InternalFooter from '../components/InternalFooter/InternalFooter';
import '../tasks/tasklist.css';
import '../users/userlist.css';
import './SubscriptionPaymentResult.css';

const SubscriptionPaymentResult = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const [status, setStatus] = useState('checking'); // checking | pending | failed
  const [message, setMessage] = useState('');

  useEffect(() => {
    const checkoutIdParam =
      getCheckoutIdFromSearchParams(searchParams) ||
      (typeof sessionStorage !== 'undefined' ? sessionStorage.getItem(PAYMENNT_CHECKOUT_ID_KEY) : null);
    const statusParam = searchParams.get('status') || searchParams.get('result');

    if (checkoutIdParam) {
      clearPaymenntCheckoutSession();
      checkPaymentStatus(checkoutIdParam);
    } else if (statusParam) {
      const s = (statusParam || '').toLowerCase();
      if (s === 'success' || s === 'paid' || s === 'completed') {
        navigate('/user-subscription', { replace: true });
        return;
      } else if (s === 'failed' || s === 'cancelled' || s === 'expired') {
        setStatus('failed');
        setMessage('Payment was not completed. Please try again.');
      } else {
        setStatus('pending');
        setMessage('Your payment is being processed. This may take a few moments.');
      }
    } else {
      setStatus('pending');
      setMessage('Redirecting back from payment. Checking your subscription status...');
      setTimeout(() => {
        refreshAndRedirect();
      }, 2000);
    }
  }, [searchParams, navigate]);

  const checkPaymentStatus = async (id) => {
    try {
      const response = await axiosInstance.get(`/payment/status/${id}`);
      const data = response.data;
      const s = (data.status || '').toLowerCase();

      if (s === 'completed' || s === 'paid') {
        navigate(
          `/subscription/payment-success?checkout=${encodeURIComponent(id)}`,
          { replace: true }
        );
        return;
      } else if (s === 'failed' || s === 'cancelled') {
        setStatus('failed');
        setMessage('Payment was not completed. Please try again.');
      } else {
        setStatus('pending');
        setMessage('Your payment is being processed. This may take a few moments.');
        setTimeout(() => checkPaymentStatus(id), 3000);
      }
    } catch (error) {
      console.error('Error checking payment status:', error);
      if (error.response?.status === 404) {
        setStatus('failed');
        setMessage('Payment session not found. Please return to subscriptions and try again.');
        return;
      }
      setStatus('pending');
      setMessage('We are verifying your payment. Please wait a moment or check your subscriptions.');
      if (id) {
        setTimeout(() => checkPaymentStatus(id), 5000);
      }
    }
  };

  const refreshSubscriptionData = () => {
    clearSubscriptionCache();
    clearFeatureCache();
    try {
      window.dispatchEvent(new Event('user-subscription-updated'));
    } catch (e) {}
  };

  const refreshAndRedirect = () => {
    refreshSubscriptionData();
    navigate('/user-subscription');
  };

  const handleBackToSubscriptions = () => {
    refreshSubscriptionData();
    navigate('/user-subscription');
  };

  const handleTryAgain = () => {
    refreshSubscriptionData();
    navigate('/user-subscription');
  };

  return (
    <div className="subscription-payment-result-page">
      <button
        type="button"
        className="payment-result-back-button"
        onClick={handleBackToSubscriptions}
      >
        <FiArrowLeft style={{ marginRight: '0.5rem' }} />
        Back
      </button>
      <Row style={{ marginBottom: '0px !important' }}>
        <Col lg={12}>
          <Card className="user-list-card payment-result-card">
            <Card.Body className="payment-result-body">
              {(status === 'checking' || status === 'pending') && (
                <>
                  <div className="result-icon pending">
                    <i className="fas fa-clock"></i>
                  </div>
                  <h4 className="payment-result-pending-title mb-3">Processing Payment</h4>
                  <p className="text-muted mb-4">{message || 'Please wait...'}</p>
                  <button type="button" className="new-user-btn payment-result-btn" onClick={handleBackToSubscriptions}>
                    <i className="fas fa-th-list me-2"></i>
                    Go to Subscriptions
                  </button>
                </>
              )}

              {status === 'failed' && (
                <>
                  <div className="result-icon failed">
                    <i className="fas fa-times-circle"></i>
                  </div>
                  <h4 className="payment-result-failed-title mb-3">Payment Not Completed</h4>
                  <p className="text-muted mb-4">{message}</p>
                  <button type="button" className="new-user-btn payment-result-btn" onClick={handleTryAgain}>
                    <i className="fas fa-redo me-2"></i>
                    Try Again
                  </button>
                </>
              )}
            </Card.Body>
          </Card>
        </Col>
      </Row>
      <InternalFooter />
    </div>
  );
};

export default SubscriptionPaymentResult;
